/**
 * zh.js - 简体中文 (Simplified Chinese) locale
 * 仅包含与 en.js 不同的键值。
 * resolveLocale 会将本文件与 en.js 进行深度合并。
 */

/** @type {Partial<import('../../../types/index.js').AsnLocale>} */
export const zh = {
  toolbar: {
    bold: '粗体 (Ctrl+B)',
    italic: '斜体 (Ctrl+I)',
    underline: '下划线 (Ctrl+U)',
    strikethrough: '删除线',
    superscript: '上标',
    subscript: '下标',
    alignLeft: '左对齐',
    alignCenter: '居中',
    alignRight: '右对齐',
    alignJustify: '两端对齐',
    ul: '无序列表',
    ol: '有序列表',
    checklist: '待办列表',
    indent: '增加缩进',
    outdent: '减少缩进',
    undo: '撤销 (Ctrl+Z)',
    redo: '重做 (Ctrl+Y)',
    hr: '水平分割线',
    link: '插入链接',
    image: '插入图片',
    video: '插入视频',
    emoji: '插入表情',
    icon: '插入 FA 图标',
    table: '插入表格',
    fontSize: '字号',
    fontSizePlaceholder: '大小',
    removeFormat: '清除格式',
    direction: '切换文字方向 (LTR / RTL)',
    fontFamily: '字体',
    paragraphStyle: '段落样式',
    paragraphStylePlaceholder: '样式',
    lineHeight: '行高',
    lineHeightPlaceholder: '\u2195 行距',
    codeview: '查看 HTML 源码',
    fullscreen: '全屏',
    shortcuts: '键盘快捷键 (Ctrl+Shift+/)',
    find: '搜索 (Ctrl+F)',
    findReplace: '查找和替换 (Ctrl+H)',
    inlineCode: '行内代码 (Ctrl+`)',
    print: '打印',
    foreColor: '文字颜色',
    backColor: '高亮颜色',
    chooseTextColor: '选择文字颜色',
    chooseHighlightColor: '选择高亮颜色',
    customColor: '自定义颜色',
    insertTableLabel: '插入表格',
    paragraphItems: {
      p:          '正文',
      blockquote: '引用',
      pre:        '代码',
    },
  },

  linkDialog: {
    ariaLabel:       '插入链接',
    title:           '插入链接',
    url:             '链接地址',
    urlPlaceholder:  'https://',
    displayText:     '显示文字',
    textPlaceholder: '链接文字',
    openInNewTab:    '在新标签页中打开',
    insertBtn:       '插入',
    cancelBtn:       '取消',
  },

  imageDialog: {
    ariaLabel:      '插入图片',
    title:          '插入图片',
    imageUrl:       '图片地址',
    urlPlaceholder: 'https://example.com/image.png',
    altText:        '替代文字',
    altPlaceholder: '图片描述',
    alignment:      '对齐方式',
    alignNone:      '无',
    alignLeft:      '左',
    alignCenter:    '居中',
    alignRight:     '右',
    uploadLabel:    '或上传文件',
    insertBtn:      '插入',
    cancelBtn:      '取消',
  },

  videoDialog: {
    ariaLabel:       '插入视频',
    title:           '插入视频',
    videoUrl:        '视频地址',
    urlPlaceholder:  'YouTube、Vimeo 或直接 .mp4 链接',
    widthLabel:      '宽度 (px)',
    widthPlaceholder: '560',
    insertBtn:       '插入',
    cancelBtn:       '取消',
    detected:        (type) => `已识别: ${type}`,
    unknownFormat:   '未知格式 \u2014 将尝试直接嵌入视频',
    invalidUrl:      'URL 无效 \u2014 请输入有效的视频链接。',
  },

  emojiDialog: {
    ariaLabel:         '插入表情',
    title:             '插入表情',
    searchPlaceholder: '搜索表情\u2026',
    all:               '全部',
    cancelBtn:         '取消',
    close:             '关闭',
    categories: {
      smileys: '笑脸',
      people:  '人物',
      animals: '动物',
      food:    '食物',
      travel:  '旅行',
      objects: '物品',
      symbols: '符号',
    },
  },

  iconDialog: {
    ariaLabel:         '插入 FA 图标',
    title:             '插入 FA 图标',
    searchPlaceholder: '搜索图标\u2026',
    all:               '全部',
    style:             '样式',
    size:              '大小',
    color:             '颜色',
    useColor:          ' 使用颜色',
    selectHint:        '请选择一个图标',
    insertBtn:         '插入 FA 图标',
    cancelBtn:         '取消',
    close:             '关闭',
    categories: {
      popular:       '热门',
      interface:     '界面',
      navigation:    '导航',
      media:         '媒体',
      communication: '通讯',
      files:         '文件',
      people:        '人物',
      objects:       '物品',
    },
  },

  findReplace: {
    findTitle:          '搜索',
    findReplaceTitle:   '查找和替换',
    findPlaceholder:    '查找\u2026',
    searchAriaLabel:    '搜索文字',
    caseSensitive:      '\u00a0区分大小写',
    prevBtn:            '\u2190 上一个',
    nextBtn:            '下一个 \u2192',
    replacePlaceholder: '替换为\u2026',
    replaceAriaLabel:   '替换为',
    replaceBtn:         '替换',
    replaceAllBtn:      '全部替换',
    close:              '\u00d7',
  },

  shortcutsDialog: {
    title:     '键盘快捷键',
    ariaLabel: '键盘快捷键',
    close:     '关闭',
    shortcuts: [
      {
        category: '文字格式',
        items: [
          { keys: 'Ctrl + B', action: '粗体' },
          { keys: 'Ctrl + I', action: '斜体' },
          { keys: 'Ctrl + U', action: '下划线' },
          { keys: 'Ctrl + K', action: '插入 / 编辑链接' },
        ],
      },
      {
        category: '历史记录',
        items: [
          { keys: 'Ctrl + Z',                      action: '撤销' },
          { keys: 'Ctrl + Y  /  Ctrl + Shift + Z', action: '重做' },
        ],
      },
      {
        category: '选择与导航',
        items: [
          { keys: 'Ctrl + A',    action: '全选' },
          { keys: 'Tab',         action: '增加缩进 / 插入空格' },
          { keys: 'Shift + Tab', action: '减少缩进' },
        ],
      },
      {
        category: '剪贴板',
        items: [
          { keys: 'Ctrl + Shift + V', action: '粘贴为纯文本' },
        ],
      },
      {
        category: '查找和替换',
        items: [
          { keys: 'Ctrl + F', action: '在文档中搜索' },
          { keys: 'Ctrl + H', action: '查找和替换' },
        ],
      },
      {
        category: '编辑器',
        items: [
          { keys: 'Ctrl + Shift + /', action: '显示快捷键对话框' },
        ],
      },
    ],
  },

  contextMenu: {
    cut:             '剪切',
    copy:            '复制',
    paste:           '粘贴',
    bold:            '粗体',
    italic:          '斜体',
    underline:       '下划线',
    textColor:       '文字颜色',
    highlightColor:  '高亮颜色',
    copyFormat:      '复制格式',
    pasteFormat:     '粘贴格式',
    removeFormat:    '清除格式',
    link:            '插入链接',
    image:           '插入图片',
    video:           '插入视频',
    table:           '插入表格',
    back:            '返回',
    noHighlight:     '无高亮',
    customColor:     '自定义颜色',
    customColorLabel: '自定义\u2026',
  },

  statusbar: {
    resizeHandle: '拖动以调整大小',
    words:      (n) => `字数: ${n}`,
    wordsLimit: (n, max) => `字数: ${n}/${max}`,
    chars:      (n) => `字符数: ${n}`,
    charsLimit: (n, max) => `字符数: ${n}/${max}`,
  },

  tooltips: {
    link: {
      ariaLabel:  '链接操作',
      openLink:   '打开链接',
      copyUrl:    '复制 URL',
      editLink:   '编辑链接',
      removeLink: '删除链接',
    },
    image: {
      ariaLabel:    '图片操作',
      label:        '图片',
      floatLeft:    '左浮动',
      noFloat:      '不浮动',
      alignCenter:  '居中对齐',
      floatRight:   '右浮动',
      originalSize: '原始尺寸',
      rotateLeft:   '向左旋转',
      rotateRight:  '向右旋转',
      cropImage:    '裁剪图片',
      addCaption:   '添加 / 编辑说明',
      deleteImage:  '删除图片',
    },
    code: {
      ariaLabel:          '代码块操作',
      label:              '代码',
      syntaxLanguage:     '语法语言',
      syntaxAriaLabel:    '语法语言',
      copyCode:           '复制代码',
      toggleWordWrap:     '切换自动换行',
      enableWordWrap:     '启用自动换行',
      disableWordWrap:    '禁用自动换行',
      convertToParagraph: '转换为段落',
      deleteCodeBlock:    '删除代码块',
    },
    table: {
      ariaLabel:          '表格操作',
      label:              '表格',
      selectCells:        '选择单元格',
      addRowAbove:        '在上方插入行',
      addRowBelow:        '在下方插入行',
      deleteRow:          '删除行',
      addColumnLeft:      '在左侧插入列',
      addColumnRight:     '在右侧插入列',
      deleteColumn:       '删除列',
      mergeCells:         '合并单元格',
      unmergeCells:       '拆分单元格',
      columnWidth:        '列宽',
      rowHeight:          '行高',
      tableBorderWidth:   '表格边框宽度',
      deleteTable:        '删除表格',
      columnWidthPx:      '列宽 (px)',
      rowHeightPx:        '行高 (px)',
      tableBorderWidthPx: '表格边框宽度 (px)',
      cancelBtn:          '取消',
      applyBtn:           '应用',
    },
    video: {
      ariaLabel:    '视频操作',
      label:        '视频',
      floatLeft:    '左浮动',
      noFloat:      '不浮动',
      alignCenter:  '居中对齐',
      floatRight:   '右浮动',
      originalSize: '原始尺寸',
      previewVideo: '预览视频',
      exitPreview:  '退出预览',
      deleteVideo:  '删除视频',
    },
  },

  errors: {
    imageFormat: (type) =>
      `格式 "${type}" 不支持在浏览器中显示。请转换为 JPEG、PNG 或 WebP。`,
    imageSize: (maxSize) =>
      `图片文件过大。最大允许大小为 ${maxSize} MB。`,
  },
};
